import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBasket, MapPin, Phone, Mail, Heart } from 'lucide-react';
import { AuthContext } from '../App';

const Footer = () => {
  const { siteSettings } = useContext(AuthContext);
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white mt-auto">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {siteSettings?.use_custom_logo && siteSettings?.site_logo_url ? (
                <img 
                  src={`${siteSettings.site_logo_url}`}
                  alt="Logo"
                  className="w-8 h-8 object-contain"
                />
              ) : (
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <ShoppingBasket className="w-5 h-5 text-white" />
                </div>
              )}
              <h3 className="text-xl font-bold">
                {siteSettings?.site_name || 'Nossa Feira'}
              </h3>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              {siteSettings?.company_description || 'Conectando produtores locais a consumidores diretos, promovendo o desenvolvimento sustentável e a economia local.'}
            </p>
            <div className="flex items-center gap-2 text-green-400">
              <Heart className="w-4 h-4" />
              <span className="text-sm">
                {siteSettings?.site_slogan || 'Feito com amor para a comunidade'}
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-green-400">Links Rápidos</h4>
            <nav className="flex flex-col space-y-2">
              <Link 
                to="/marketplace" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Marketplace
              </Link>
              <Link 
                to="/login" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Entrar
              </Link>
              <Link 
                to="/register" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Cadastrar-se
              </Link>
              <Link 
                to="/sobre" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Quem Somos
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-green-400">Contato</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <MapPin className="w-4 h-4 text-green-400" />
                <span>{siteSettings?.contact_address || 'Bom Jesus da Lapa, BA'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <Phone className="w-4 h-4 text-green-400" />
                <span>{siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <Mail className="w-4 h-4 text-green-400" />
                <span>{siteSettings?.contact_email || 'contato@nossafeira.com'}</span>
              </div>
              {siteSettings?.whatsapp_business && (
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Phone className="w-4 h-4 text-green-400" />
                  <a 
                    href={`https://wa.me/${siteSettings.whatsapp_business.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors"
                  >
                    WhatsApp Business
                  </a>
                </div>
              )}
            </div>
            
            {/* Social Media Links */}
            {(siteSettings?.facebook_url || siteSettings?.instagram_url || siteSettings?.twitter_url || siteSettings?.linkedin_url || siteSettings?.youtube_url) && (
              <div className="pt-4">
                <h5 className="text-sm font-medium text-green-400 mb-2">Redes Sociais</h5>
                <div className="flex flex-wrap gap-3">
                  {siteSettings?.facebook_url && (
                    <a
                      href={siteSettings.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-green-400 transition-colors text-sm"
                    >
                      Facebook
                    </a>
                  )}
                  {siteSettings?.instagram_url && (
                    <a
                      href={siteSettings.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-green-400 transition-colors text-sm"
                    >
                      Instagram
                    </a>
                  )}
                  {siteSettings?.twitter_url && (
                    <a
                      href={siteSettings.twitter_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-green-400 transition-colors text-sm"
                    >
                      Twitter
                    </a>
                  )}
                  {siteSettings?.linkedin_url && (
                    <a
                      href={siteSettings.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-green-400 transition-colors text-sm"
                    >
                      LinkedIn
                    </a>
                  )}
                  {siteSettings?.youtube_url && (
                    <a
                      href={siteSettings.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-green-400 transition-colors text-sm"
                    >
                      YouTube
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Legal Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-green-400">Legal</h4>
            <nav className="flex flex-col space-y-2">
              <Link 
                to="/termos-de-uso" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Termos de Uso
              </Link>
              <Link 
                to="/politica-de-privacidade" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Política de Privacidade
              </Link>
              <Link 
                to="/cookies" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Política de Cookies
              </Link>
              <Link 
                to="/ajuda" 
                className="text-gray-300 hover:text-green-400 transition-colors duration-200 text-sm"
              >
                Central de Ajuda
              </Link>
            </nav>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © {currentYear} {siteSettings?.site_name || 'Nossa Feira'}. Todos os direitos reservados.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                {siteSettings?.company_description || 'Plataforma desenvolvida para conectar produtores e consumidores locais'}
              </p>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <span>Versão {siteSettings?.version || '1.0.0'}</span>
              <span>•</span>
              <span>{siteSettings?.contact_address || 'Brasil • Bahia'}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;